import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { PRES } from '../chrome';

interface SteppedNumberFieldProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  title?: string;
  disabled?: boolean;
  width?: number | string;
  compact?: boolean;
}

export function clampNumber(value: number, min?: number, max?: number): number {
  let next = value;
  if (min != null) next = Math.max(min, next);
  if (max != null) next = Math.min(max, next);
  return next;
}

function formatValue(value: number, step: number): string {
  if (!Number.isFinite(value)) return '';
  if (Number.isInteger(step)) return String(Math.round(value));
  return String(Number(value.toFixed(4)));
}

export function parseNumberDraft(raw: string): number | null {
  const trimmed = raw.trim().replace(',', '.');
  if (trimmed === '' || trimmed === '-' || trimmed === '.' || trimmed === '-.') return null;
  if (!/^-?\d*\.?\d*$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

const SteppedNumberField: React.FC<SteppedNumberFieldProps> = ({
  value,
  onChange,
  min,
  max,
  step = 1,
  label,
  title,
  disabled,
  width = 64,
  compact,
}) => {
  const [draft, setDraft] = useState<string | null>(null);

  useEffect(() => {
    setDraft(null);
  }, [value]);

  const commit = (raw: string) => {
    const parsed = parseNumberDraft(raw);
    setDraft(null);
    if (parsed == null) return;
    onChange(clampNumber(parsed, min, max));
  };

  return (
    <TextField
      size="small"
      label={label}
      title={title || label}
      disabled={disabled}
      value={draft ?? formatValue(value, step)}
      onMouseDown={(event) => event.stopPropagation()}
      onPointerDown={(event) => event.stopPropagation()}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={(event) => commit(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit((event.target as HTMLInputElement).value);
          (event.target as HTMLInputElement).blur();
        }
      }}
      sx={{
        width,
        flexShrink: 0,
        '& input': {
          MozAppearance: 'textfield',
        },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
          WebkitAppearance: 'none',
          margin: 0,
        },
        '& .MuiOutlinedInput-root': {
          bgcolor: PRES.white,
          borderRadius: 2,
        },
        '& .MuiOutlinedInput-input': {
          py: compact ? 0.55 : 0.7,
          px: 1,
          fontSize: 13,
          lineHeight: 1.45,
          textAlign: 'center',
          fontVariantNumeric: 'tabular-nums',
        },
      }}
    />
  );
};

export default SteppedNumberField;
